/**
 * useEntitlements — single source of truth for "what features does the
 * current user have?" The hook batches:
 *   • Paid plan features (via GET /billing/me → composition.features)
 *   • Active grants (via GET /grants/me → grants[].features)
 *
 * Both queries are tagged with the same staleTime and refetchOnFocus so
 * UI components don't flicker between "PRO" and "OK" states on a tab
 * switch. Returns:
 *   { features: Set<string>, has(key): bool, isLoading: bool }
 *
 * Note: server is still the authoritative gate — this hook only powers
 * VISUAL hints (badges, button disables). A user with devtools can
 * "unlock" the UI but the API will still 402 them.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "../lib/api";

export function useEntitlements() {
  const { data: billing, isLoading: bLoading } = useQuery({
    queryKey: ["billing-me"],
    queryFn: async () => (await api.get("/billing/me")).data,
    staleTime: 60_000,
    retry: false,
  });
  const { data: grants, isLoading: gLoading } = useQuery({
    queryKey: ["my-grants"],
    queryFn: async () => (await api.get("/grants/me")).data,
    staleTime: 60_000,
    retry: false,
  });

  const features = useMemo(() => {
    const out = new Set();
    // 1) Paid plan features — multiple historical shapes accepted:
    //    a) billing.composition.features
    //    b) billing.features (older)
    //    c) billing.plan.features (current — also where bonus plan
    //       lands when via_grant=true since /billing/me promotes the
    //       grant to `plan` when there's no paid composition).
    const planFeatures = billing?.composition?.features
      || billing?.features
      || billing?.plan?.features
      || [];
    for (const f of planFeatures) out.add(f);

    // 2) Grants — `/grants/me` now hydrates `target_features` for
    //    plan/addon grants. We accept ALL kinds: feature grants
    //    contribute the single feature; plan/addon grants contribute
    //    the whole plan's feature set. Falls back to {kind:"feature",
    //    target_id} for older API responses without target_features.
    for (const g of grants?.grants || []) {
      const hydrated = g.target_features;
      if (Array.isArray(hydrated) && hydrated.length > 0) {
        for (const f of hydrated) out.add(f);
      } else if (g.kind === "feature" && g.target_id) {
        out.add(g.target_id);
      }
    }
    return out;
  }, [billing, grants]);

  return {
    features,
    has: (key) => features.has(key),
    isLoading: bLoading || gLoading,
  };
}
